import { useState, type DragEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AdjustmentsHorizontalIcon,
  ArrowsUpDownIcon, ChevronUpIcon, ChevronDownIcon, ChevronDoubleUpIcon, ChevronDoubleLeftIcon, Bars2Icon,
} from '@heroicons/react/24/outline';
import { PaneRail } from '../../search/PaneControls';
import { CAMERA_TAG_COLORS } from '../CameraTagChip';
import {
  PTZ_FILTER_OPTIONS, type CameraTag, type CameraTagCatalog, type PtzFilter, type SelectedCameraTag,
} from './cameraTagsApi';
import { PAGE_FILL_H } from '../../layout/TitleBar';
import {
  filterColumnFrame, useFilterFlush, FilterSection, FilterCheck, FilterSearch, FilterChips,
} from '../../layout/FilterColumn';
import { FilterDropdown, OptionList } from '../../search/FilterControls';

/** Past this many tags a category is one searchable row, as Search & Track's long lists are. */
const TAGS_INLINE_MAX = 6;

/**
 * The camera list's filter column, on both dashboards — the same shape as the Search & Track
 * page: labelled sections on the left that fold to a rail, the list beside it.
 *
 * Fully controlled. Each app keeps the filter state where it already was (the local app in its
 * cameras slice, the cloud in its page) and passes it in; the group tree arrives as a slot
 * because each app renders its own.
 *
 * Within one tag category ANY selected tag matches; categories, groups and PTZ must ALL match.
 */

export interface CameraFiltersColumnProps {
  open: boolean;
  onToggle: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** The app's group tree (with checkboxes). */
  groupsSlot: ReactNode;
  groupCount: number;
  onClearGroups: () => void;
  catalog: CameraTagCatalog;
  selectedTags: SelectedCameraTag[];
  onToggleTag: (tag: SelectedCameraTag) => void;
  onSetTags: (tags: SelectedCameraTag[]) => void;
  ptz: PtzFilter | string;
  onPtzChange: (value: PtzFilter) => void;
  onClearAll: () => void;
  /** Where tags are managed; the link is shown only when given (i.e. the user may edit). */
  manageTagsHref?: string;
  /** Height classes — the column fills the screen under each app's own header. */
  heightClass?: string;
}

/**
 * The operator's own section order — "Department at the top" — per browser, not per deployment:
 * it is a reading preference, needs no edit permission, and works the same on the cloud. Keys
 * are `groups`, `ptz` and `tag:<category id>`; a key the catalogue no longer has is skipped and
 * a section the saved order does not know (a new category, another site's) goes after the rest
 * in its default place. Name search is not in it — it always stays first.
 */
const ORDER_KEY = 'trinetra.cameras.filterOrder';

function readOrder(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(ORDER_KEY) || '[]');
    return Array.isArray(v) ? v.filter((k): k is string => typeof k === 'string') : [];
  } catch { return []; }
}

function writeOrder(order: string[] | null) {
  try {
    if (order) localStorage.setItem(ORDER_KEY, JSON.stringify(order));
    else localStorage.removeItem(ORDER_KEY);
  } catch { /* private window — the order just lasts for this visit */ }
}

/** Saved keys that still exist, in their saved order, then everything else in default order. */
export function arrangeSections(defaultKeys: string[], saved: string[]): string[] {
  const known = new Set(defaultKeys);
  const head = saved.filter((k, i) => known.has(k) && saved.indexOf(k) === i);
  const placed = new Set(head);
  return [...head, ...defaultKeys.filter((k) => !placed.has(k))];
}

/** One section's title row while arranging: drag handle, name, and move buttons. */
function ArrangeRow({ title, index, count, onMove, dragging, onDragStart, onDragEnter, onDragEnd }: {
  title: string; index: number; count: number; onMove: (to: number) => void;
  dragging: boolean; onDragStart: () => void; onDragEnter: () => void; onDragEnd: () => void;
}) {
  const btn = 'rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30';
  return (
    <div
      draggable
      onDragStart={(e: DragEvent) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', title); onDragStart(); }}
      onDragEnter={onDragEnter}
      onDragOver={(e) => e.preventDefault()}
      onDragEnd={onDragEnd}
      onDrop={(e) => e.preventDefault()}
      className={`flex cursor-grab items-center gap-1.5 rounded-md border px-2 py-1.5 active:cursor-grabbing ${
        dragging ? 'border-brand bg-brand-light' : 'border-border bg-card'
      }`}
    >
      <Bars2Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-foreground">{title}</span>
      <button type="button" className={btn} disabled={index === 0} onClick={() => onMove(0)}
        aria-label={`Move ${title} to the top`} title="Move to top">
        <ChevronDoubleUpIcon className="h-3.5 w-3.5" />
      </button>
      <button type="button" className={btn} disabled={index === 0} onClick={() => onMove(index - 1)}
        aria-label={`Move ${title} up`} title="Move up">
        <ChevronUpIcon className="h-3.5 w-3.5" />
      </button>
      <button type="button" className={btn} disabled={index === count - 1} onClick={() => onMove(index + 1)}
        aria-label={`Move ${title} down`} title="Move down">
        <ChevronDownIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function CameraFiltersColumn({
  open, onToggle, search, onSearchChange, groupsSlot, groupCount, onClearGroups,
  catalog, selectedTags, onToggleTag, onSetTags, ptz, onPtzChange, onClearAll, manageTagsHref,
  heightClass = PAGE_FILL_H,
}: CameraFiltersColumnProps) {
  const flush = useFilterFlush();
  const selectedTagIds = new Set(selectedTags.map((t) => t.id));
  const activeCount = (search ? 1 : 0) + groupCount + selectedTags.length + (ptz ? 1 : 0);
  const categories = catalog.categories.filter((c) => c.tags.length > 0);

  const [savedOrder, setSavedOrder] = useState<string[]>(readOrder);
  const [arranging, setArranging] = useState(false);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const defaultKeys = ['groups', 'ptz', ...categories.map((c) => `tag:${c.id}`)];
  const order = arrangeSections(defaultKeys, savedOrder);
  const customOrder = order.join('|') !== defaultKeys.join('|');
  const saveOrder = (next: string[]) => { setSavedOrder(next); writeOrder(next); };
  const moveSection = (key: string, to: number) => {
    const next = order.filter((k) => k !== key);
    next.splice(Math.max(0, Math.min(to, next.length)), 0, key);
    saveOrder(next);
  };
  const resetOrder = () => { setSavedOrder([]); writeOrder(null); };

  const textLink = (label: string, onClick: () => void) => (
    <button type="button" onClick={onClick} className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
      {label}
    </button>
  );
  const clearLink = (onClick: () => void, n?: number) => textLink(`Clear${n ? ` (${n})` : ''}`, onClick);

  const catById = new Map(categories.map((c) => [c.id, c]));
  const sectionTitle = (key: string) =>
    key === 'groups' ? 'Groups' : key === 'ptz' ? 'Camera type' : catById.get(key.slice(4))?.name || 'Tags';

  const renderSection = (key: string): ReactNode => {
    if (key === 'groups') return (
      <FilterSection
        title="Groups"
        hint={groupCount > 0 ? `${groupCount} selected · incl. sub-groups` : 'incl. sub-groups'}
        action={groupCount > 0 ? clearLink(onClearGroups) : null}
      >
        <div className="max-h-64 overflow-y-auto rounded-lg border border-border p-1" style={{ scrollbarWidth: 'thin' }}>
          {groupsSlot}
        </div>
      </FilterSection>
    );
    // A few short choices: one-click chips, as the Alert List's severity — the count rides in the label.
    if (key === 'ptz') return (
      <FilterSection title="Camera type" action={ptz ? clearLink(() => onPtzChange('')) : null}>
        <FilterChips<PtzFilter>
          cols={2}
          value={(ptz || '') as PtzFilter}
          onChange={onPtzChange}
          options={PTZ_FILTER_OPTIONS.map((o) => ({
            value: o.value,
            label: o.value ? `${o.label} · ${catalog.ptz[o.value] ?? 0}` : o.label,
            title: o.value ? `${o.label}: ${catalog.ptz[o.value] ?? 0} cameras` : 'Every camera type',
          }))}
        />
      </FilterSection>
    );
    const cat = catById.get(key.slice(4));
    if (!cat) return null;
    const picked = cat.tags.filter((t) => selectedTagIds.has(t.id));
    const tagObj = (t: CameraTag): SelectedCameraTag =>
      ({ id: t.id, name: t.name, color: t.color, category_id: cat.id, category_name: cat.name });
    const swatchOf = (t: CameraTag) => CAMERA_TAG_COLORS[t.color || ''] || CAMERA_TAG_COLORS.slate;
    return (
      <FilterSection
        title={cat.name}
        hint={cat.multi_select ? 'any of' : 'one per camera'}
        action={picked.length > 0 ? clearLink(() => onSetTags(selectedTags.filter((t) => t.category_id !== cat.id))) : null}
      >
        {cat.tags.length > TAGS_INLINE_MAX ? (
          // A long category: the Search & Track row, its list opening with a search box.
          <FilterDropdown
            variant="row"
            label={cat.name}
            summary={picked.length === 0 ? 'All' : picked.length === 1 ? picked[0].name : `${picked.length} selected`}
            active={picked.length > 0}
            panelClassName="w-72"
          >
            {() => (
              <OptionList
                mode="multi"
                searchable
                searchPlaceholder={`Search ${cat.name.toLowerCase()}…`}
                options={cat.tags.map((t) => ({
                  value: t.id,
                  label: t.name,
                  count: t.camera_count ?? 0,
                  adornment: <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: swatchOf(t) }} />,
                }))}
                selected={picked.map((t) => t.id)}
                onChange={(next) => onSetTags([
                  ...selectedTags.filter((t) => t.category_id !== cat.id),
                  ...cat.tags.filter((t) => next.includes(t.id)).map(tagObj),
                ])}
              />
            )}
          </FilterDropdown>
        ) : (
          <div className="space-y-0.5">
            {cat.tags.map((t) => (
              <FilterCheck
                key={t.id}
                checked={selectedTagIds.has(t.id)}
                onClick={() => onToggleTag(tagObj(t))}
                label={t.name}
                count={t.camera_count ?? 0}
                swatch={swatchOf(t)}
              />
            ))}
          </div>
        )}
      </FilterSection>
    );
  };

  // The Search & Track panel's width — the same `wide` FilterColumn the Alert List uses.
  const w = 'w-[19rem] xl:w-[20.5rem]';
  const squareBtn = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors';

  return (
    // Width animates between the column and a rail; the controls stay mounted while folded so a
    // half-typed search survives a fold (same as the Search page). Sticky + full height, so it
    // stays beside the list as the list scrolls and scrolls inside itself.
    <div
      data-tour="cameras-filters"
      className={`${filterColumnFrame(flush, heightClass)} transition-[width] duration-300 ease-in-out ${
        open ? w : 'w-11'
      }`}
    >
      <aside
        aria-label="Camera filters"
        aria-hidden={!open}
        className={`flex h-full flex-col transition-opacity duration-200 ${w} ${open ? 'opacity-100' : 'invisible opacity-0'}`}
      >
        {/* The Search & Track panel's heading (FilterColumn's): FILTERS, how many are on,
            "Clear all", then Arrange and a square « to fold. */}
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-2.5">
          <span className="flex min-w-0 items-center gap-2 truncate text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
            <AdjustmentsHorizontalIcon className="h-4 w-4 shrink-0" />
            Filters
          </span>
          {activeCount > 0 && (
            <span className="min-w-[1.25rem] shrink-0 rounded-full bg-brand-light px-1.5 text-center text-[11px] font-semibold leading-5 text-brand">{activeCount}</span>
          )}
          <span className="flex-1" />
          {activeCount > 0 && (
            <button type="button" onClick={onClearAll} className="shrink-0 text-[12px] text-muted-foreground transition-colors hover:text-foreground">
              Clear all
            </button>
          )}
          {order.length > 1 && (
            <button type="button" onClick={() => setArranging((v) => !v)} aria-pressed={arranging}
              title="Change the order of the filter sections" aria-label="Arrange filter sections"
              className={`${squareBtn} ${arranging ? 'border-brand bg-brand-light text-brand' : 'border-border text-muted-foreground hover:border-brand hover:text-brand'}`}>
              <ArrowsUpDownIcon className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={onToggle} title="Hide camera filters" aria-label="Hide camera filters"
            className={`${squareBtn} border-border text-muted-foreground hover:border-brand hover:text-brand`}>
            <ChevronDoubleLeftIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4" style={{ scrollbarWidth: 'thin' }}>
          {/* Search first, always — it is not one of the arrangeable sections. */}
          <FilterSearch value={search} onChange={onSearchChange} placeholder="Search cameras…" />

          {/* Sections in the operator's order — see ORDER_KEY. Arranging shows titles only, so a
              long tag list does not push the next section off screen mid-drag. */}
          {arranging ? (
            <div className="space-y-1.5" role="list" aria-label="Filter sections order">
              <p className="px-0.5 text-[11px] text-muted-foreground">Drag, or use the arrows. Saved in this browser.</p>
              {order.map((key, i) => (
                <ArrangeRow
                  key={key}
                  title={sectionTitle(key)}
                  index={i}
                  count={order.length}
                  onMove={(to) => moveSection(key, to)}
                  dragging={dragKey === key}
                  onDragStart={() => setDragKey(key)}
                  onDragEnter={() => { if (dragKey && dragKey !== key) moveSection(dragKey, i); }}
                  onDragEnd={() => setDragKey(null)}
                />
              ))}
              <div className="flex items-center justify-between px-0.5 pt-1">
                {customOrder ? textLink('Reset to default', resetOrder) : <span />}
                <button type="button" onClick={() => setArranging(false)}
                  className="rounded-lg bg-brand px-2.5 py-1 text-[12px] font-medium text-white transition-colors hover:bg-brand-dark">
                  Done
                </button>
              </div>
            </div>
          ) : order.map((key) => <div key={key}>{renderSection(key)}</div>)}

          {manageTagsHref && (
            <Link to={manageTagsHref} className="block px-1 text-[12px] font-medium text-brand hover:underline">
              {categories.length ? 'Manage camera tags' : 'Create tags such as departments or indoor/outdoor'}
            </Link>
          )}
        </div>
      </aside>

      {/* Folded: a way back in, with the number of filters still narrowing the list. */}
      {!open && (
        <PaneRail
          label="Camera filters"
          Icon={AdjustmentsHorizontalIcon}
          count={activeCount}
          onExpand={onToggle}
          opens="right"
          className="absolute inset-0"
        />
      )}
    </div>
  );
}

/** Toggle one tag in a selection list — what a click in the column means. */
export function toggleSelectedTag(list: SelectedCameraTag[], tag: SelectedCameraTag): SelectedCameraTag[] {
  return list.some((t) => t.id === tag.id) ? list.filter((t) => t.id !== tag.id) : [...list, tag];
}
